// @ts-nocheck
import React, { useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Avatar,
} from '@mui/material';
import { Close, Star, StarBorder, StarHalf } from '@mui/icons-material';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Review {
  id: number;
  rating: number;
  comment?: string;
  createdAt: string;
  reviewer_name?: string;
  customer?: { name?: string };
}

interface ReviewsModalProps {
  open: boolean;
  onClose: () => void;
  reviews: Review[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function calcAvg(reviews: Review[]): number {
  if (!reviews.length) return 0;
  return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
}

function calcDist(reviews: Review[]): { star: number; count: number; pct: number }[] {
  return [5, 4, 3, 2, 1].map(star => {
    const count = reviews.filter(r => r.rating === star).length;
    return { star, count, pct: reviews.length ? (count / reviews.length) * 100 : 0 };
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function getInitial(name: string): string {
  return (name || 'C').trim()[0].toUpperCase();
}

function getDisplayName(r: Review): string {
  return r.reviewer_name || r.customer?.name || 'Cliente';
}

const AVATAR_COLORS = [
  '#4f46e5','#0891b2','#059669','#d97706','#dc2626',
  '#7c3aed','#db2777','#0284c7','#16a34a','#ca8a04',
];

function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StarRow({ value, max = 5 }: { value: number; max?: number }) {
  return (
    <Box sx={{ display: 'flex', gap: '2px' }}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i + 1 <= Math.floor(value);
        const half   = !filled && i < value;
        return filled
          ? <Star    key={i} sx={{ fontSize: 16, color: '#f59e0b' }} />
          : half
          ? <StarHalf key={i} sx={{ fontSize: 16, color: '#f59e0b' }} />
          : <StarBorder key={i} sx={{ fontSize: 16, color: '#d1d5db' }} />;
      })}
    </Box>
  );
}

function RatingSummary({ avg, total, dist }: {
  avg: number; total: number;
  dist: { star: number; count: number; pct: number }[];
}) {
  return (
    <Box sx={{
      display: 'flex', gap: 3, alignItems: 'center',
      p: 2.5, mb: 2,
      bgcolor: '#fafafa', borderRadius: 3,
      border: '1px solid #f1f5f9',
    }}>
      {/* Big number */}
      <Box sx={{ textAlign: 'center', minWidth: 72 }}>
        <Typography sx={{ fontSize: 52, fontWeight: 900, lineHeight: 1, color: '#111827', letterSpacing: '-2px' }}>
          {avg.toFixed(1)}
        </Typography>
        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
          <StarRow value={avg} />
        </Box>
        <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5, fontWeight: 500 }}>
          {total} avaliações
        </Typography>
      </Box>

      {/* Bars */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.7 }}>
        {dist.map(({ star, count, pct }) => (
          <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#6b7280', width: 8, textAlign: 'right', fontWeight: 600 }}>
              {star}
            </Typography>
            <Star sx={{ fontSize: 11, color: '#f59e0b' }} />
            <Box sx={{ flex: 1, height: 7, bgcolor: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
              <Box sx={{
                width: `${pct}%`, height: '100%',
                bgcolor: pct > 0 ? '#f59e0b' : 'transparent',
                borderRadius: 99,
                transition: 'width 0.6s ease',
              }} />
            </Box>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', width: 18, textAlign: 'right' }}>
              {count}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ReviewItem({ review }: { review: Review }) {
  const name  = getDisplayName(review);
  const color = avatarColor(name);

  return (
    <Box sx={{
      display: 'flex', gap: 1.5,
      py: 2,
      borderBottom: '1px solid #f3f4f6',
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* Avatar */}
      <Avatar sx={{
        width: 42, height: 42, flexShrink: 0,
        bgcolor: color, color: '#fff',
        fontWeight: 700, fontSize: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      }}>
        {getInitial(name)}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 1, flexShrink: 0, mt: '2px' }}>
            {formatDate(review.createdAt)}
          </Typography>
        </Box>

        <Box sx={{ mb: review.comment ? 0.8 : 0 }}>
          <StarRow value={review.rating} />
        </Box>

        {review.comment && (
          <Typography sx={{
            fontSize: 13, color: '#374151', lineHeight: 1.6,
            display: '-webkit-box', WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {review.comment}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReviewsModal({ open, onClose, reviews }: ReviewsModalProps) {
  const avg  = useMemo(() => calcAvg(reviews), [reviews]);
  const dist = useMemo(() => calcDist(reviews), [reviews]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          mx: { xs: 1.5, sm: 2 },
          maxHeight: '90vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, pt: 2.5, pb: 1.5,
        borderBottom: '1px solid #f3f4f6',
      }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#111827' }}>
          Avaliações
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{
          color: '#9ca3af',
          '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
        }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
        {/* Rating Summary */}
        {reviews.length > 0 && (
          <RatingSummary avg={avg} total={reviews.length} dist={dist} />
        )}

        {/* Empty state */}
        {reviews.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Star sx={{ fontSize: 48, color: '#e5e7eb', mb: 1 }} />
            <Typography color="text.secondary" fontWeight={600}>
              Nenhuma avaliação ainda
            </Typography>
          </Box>
        )}

        {/* List */}
        <Box>
          {reviews.map(r => <ReviewItem key={r.id} review={r} />)}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
